import { useState } from 'react';
import { Copy, Check, Download, Lock } from 'lucide-react';
import {
  buildWrittenHandover,
  buildRadioHandover,
  exportAuditCsv,
  exportFilename,
  type Incident,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { useRole } from '@/auth/role';
import { downloadText } from '@/incident/download';

/**
 * Handover & audit export. Deterministic — composed from the structured incident,
 * the live insights and the audit log; works fully with AI off. Gated by the
 * `export.generate` capability (crew leaders capture but don't export handovers).
 */
export function ExportPanel({ incident }: { incident: Incident }) {
  const { insights, audit, logEvent } = useIncidentStore();
  const { can } = useRole();

  if (!can('export.generate')) {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-muted">
        <Lock size={13} /> Your role can view the incident but not generate handover exports.
      </p>
    );
  }

  const written = buildWrittenHandover(incident, insights);
  const radio = buildRadioHandover(incident, insights);
  const csv = exportAuditCsv(audit);

  return (
    <div className="space-y-3">
      <ExportBlock
        title="Written handover"
        text={written}
        filename={`${exportFilename(incident, 'handover')}.txt`}
        onExport={() => logEvent('export.generated', { kind: 'written-handover' })}
        multiline
      />
      <ExportBlock
        title="Radio handover"
        text={radio}
        filename={`${exportFilename(incident, 'radio-handover')}.txt`}
        onExport={() => logEvent('export.generated', { kind: 'radio-handover' })}
      />
      <ExportBlock
        title="Audit log (CSV)"
        text={csv}
        filename={`${exportFilename(incident, 'audit')}.csv`}
        mime="text/csv"
        rows={audit.length}
        onExport={() => logEvent('export.generated', { kind: 'audit-csv' })}
      />
      <p className="text-[11px] italic text-muted">
        Generated deterministically for handover and the post-incident record. Advisory only —
        confirm all facts before acting.
      </p>
    </div>
  );
}

function ExportBlock({
  title,
  text,
  filename,
  mime = 'text/plain',
  multiline = false,
  rows,
  onExport,
}: {
  title: string;
  text: string;
  filename: string;
  mime?: string;
  multiline?: boolean;
  rows?: number;
  onExport?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — download is available */
    }
  };
  return (
    <div className="rounded-lg border border-border bg-surface-2/60">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="text-xs font-semibold text-muted">
          {title}
          {typeof rows === 'number' && <span className="ml-1 text-[10px]">· {rows} events</span>}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={copy}
            className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent"
            aria-label={`Copy ${title}`}
          >
            {copied ? <Check size={13} className="text-ok" /> : <Copy size={13} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={() => {
              downloadText(filename, text, mime);
              onExport?.();
            }}
            className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent"
            aria-label={`Download ${title}`}
          >
            <Download size={13} /> Download
          </button>
        </div>
      </div>
      <pre
        className={`max-h-48 overflow-auto px-3 py-2 text-sm text-text/90 ${
          multiline ? 'whitespace-pre-wrap' : 'whitespace-pre-wrap'
        } font-sans`}
      >
        {text}
      </pre>
    </div>
  );
}
